import React, {useEffect, useMemo, useRef, useState} from "react";
import Konva from "konva";
import {Vector2d} from "konva/types/types";
import {distance, getStageBorderPosition} from "../../../project-drawings/features/editor/utils";
import {KonvaEventType, ZoomState} from "../../types";
import {MAX_SCALE} from "../../../project-drawings/features/editor/constants";
import throttle from "lodash/throttle";

type UseBasePanProps = {
	stageRef: React.RefObject<Konva.Stage>;
	canScroll: () => boolean;
	viewportSize: { width: number, height: number }
	zoomState?: ZoomState
	setZoomState?: (zoomState: ZoomState) => void
}

function getPinchDistance(touches: TouchList): number {
	return Math.hypot(
		touches[1].clientX - touches[0].clientX,
		touches[1].clientY - touches[0].clientY
	)
}

export function useBasePan({stageRef, canScroll, viewportSize, zoomState, setZoomState}: UseBasePanProps) {
	const [panInProgress, setPanInProgress] = useState(false);
	const panStartVector = useRef<Vector2d>({x: 0, y: 0});
	const pinchStartDistance = useRef<number>(0)
	const pinchStartScale = useRef<number>(1)
	const pinchStartMidpoint = useRef<{x: number, y: number}>({x: 0, y: 0})
	const pinchStartStagePosition = useRef<{x: number, y: number}>({x: 0, y: 0})
	const pinchStartClientRect = useRef<{x: number, y: number, width: number, height: number}>({x: 0, y: 0, width: 0, height: 0})
	const isPinching = useRef<boolean>(false)
	const latestPinchScale = useRef<number>(1)
	const panCleanupListeners = useRef<(() => void) | null>(null)
	const zoomStateRef = useRef(zoomState)
	const setZoomStateRef = useRef(setZoomState)

	useEffect(() => {
		zoomStateRef.current = zoomState
	}, [zoomState])

	useEffect(() => {
		setZoomStateRef.current = setZoomState
	}, [setZoomState])

	const throttledSetZoomState = useMemo(() => throttle(
		(zs: ZoomState) => setZoomStateRef.current?.(zs),
		50, {leading: true, trailing: false}
	), [])

	const shouldScroll = (event: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
		const stage = stageRef.current;
		return canScroll() && (event.target === stage || event.target.name().startsWith("background"));
	};

	const handleMove = (
		clientX: number,
		clientY: number,
		previousClientX: number,
		previousClientY: number,
		event: KonvaEventType,
	) => {
		const movementDistance = distance({x: clientX, y: clientY}, panStartVector.current)
		if (movementDistance > 1) {
			setPanInProgress(true)
		}
		event.preventDefault();

		const deltaX = previousClientX - clientX;
		const deltaY = previousClientY - clientY;

		const stage = stageRef.current;
		if (stage) {
			let newX = stage.x() - deltaX
			let newY = stage.y() - deltaY

			stage.position(getStageBorderPosition(
				{x: newX, y: newY},
				stage.getClientRect(),
				viewportSize
			))
			stage.batchDraw()
		}
	};

	const onMouseDown = (event: Konva.KonvaEventObject<MouseEvent>) => {
		const mouseDownEvent = event.evt;

		if (shouldScroll(event)) {
			mouseDownEvent.preventDefault();

			let previousClientX = mouseDownEvent.clientX
			let previousClientY = mouseDownEvent.clientY
			panStartVector.current = {
				x: previousClientX,
				y: previousClientY
			}

			const onMouseMove = (mouseMoveEvent: MouseEvent) => {
				handleMove(
					mouseMoveEvent.clientX,
					mouseMoveEvent.clientY,
					previousClientX,
					previousClientY,
					mouseMoveEvent
				);
				previousClientX = mouseMoveEvent.clientX
				previousClientY = mouseMoveEvent.clientY
			}

			const onMouseUp = () => {
				setPanInProgress(false);
				window.removeEventListener("mouseup", onMouseUp)
				window.removeEventListener("mousemove", onMouseMove)
			}

			window.addEventListener("mouseup", onMouseUp)
			window.addEventListener("mousemove", onMouseMove)
		}
	}

	const onTouchStart = (event: Konva.KonvaEventObject<TouchEvent>) => {
		const touchStartEvent = event.evt;
		// Prevent browser from synthesizing mouse events from touch. Without this, both
		// onTouchEnd and synthesized onMouseUp fire per tap, causing double-click counters
		// to trigger immediately and drawing tools to receive duplicate events.
		touchStartEvent.preventDefault();
		const stage = stageRef.current;
		const touches = touchStartEvent.touches;

		if (touches.length >= 2 && setZoomState && stage) {
			// Two-finger: pinch-zoom + translational pan — always allowed regardless of active tool

			// Cancel any in-progress 1-finger pan so its listeners don't conflict
			if (panCleanupListeners.current) {
				panCleanupListeners.current();
				panCleanupListeners.current = null;
				setPanInProgress(false);
			}

			touchStartEvent.preventDefault();
			pinchStartDistance.current = getPinchDistance(touches)
			pinchStartScale.current = zoomStateRef.current?.scale ?? 1
			isPinching.current = true
			pinchStartMidpoint.current = {
				x: (touches[0].clientX + touches[1].clientX) / 2,
				y: (touches[0].clientY + touches[1].clientY) / 2,
			}
			pinchStartStagePosition.current = {x: stage.x(), y: stage.y()}
			// Cache clientRect at start — getClientRect() traverses the full scene graph so
			// calling it at 60-120Hz causes jank. During pinch, React hasn't re-rendered with
			// the new scale yet so a live call would return stale data anyway.
			pinchStartClientRect.current = stage.getClientRect()
			latestPinchScale.current = pinchStartScale.current

			const onPinchMove = (e: TouchEvent) => {
				if (e.touches.length >= 2) {
					e.preventDefault()
					const currentZoom = zoomStateRef.current
					if (!currentZoom || !stage) return
					if (pinchStartDistance.current === 0) return

					const currentDistance = getPinchDistance(e.touches)
					const rawScale = pinchStartScale.current * (currentDistance / pinchStartDistance.current)
					const newScale = Math.max(currentZoom.minScale, Math.min(MAX_SCALE, rawScale))
					latestPinchScale.current = newScale

					// Throttle Redux dispatch — complex drawings re-render all connected components
					// on every dispatch; 50ms matches the wheel-zoom throttle
					throttledSetZoomState({...currentZoom, scale: newScale})

					// Keep the pinch-start midpoint fixed in stage space under the current finger
					// midpoint. Must compensate for both scale change and translational pan.
					const currentMidX = (e.touches[0].clientX + e.touches[1].clientX) / 2
					const currentMidY = (e.touches[0].clientY + e.touches[1].clientY) / 2
					const scaleRatio = newScale / pinchStartScale.current
					const newX = currentMidX - (pinchStartMidpoint.current.x - pinchStartStagePosition.current.x) * scaleRatio
					const newY = currentMidY - (pinchStartMidpoint.current.y - pinchStartStagePosition.current.y) * scaleRatio
					stage.position(getStageBorderPosition(
						{x: newX, y: newY},
						pinchStartClientRect.current,
						viewportSize
					))
					stage.batchDraw()
				}
			}

			const onPinchEnd = (e: TouchEvent) => {
				if (e.touches.length < 2) {
					isPinching.current = false
					window.removeEventListener("touchend", onPinchEnd)
					window.removeEventListener("touchcancel", onPinchEnd)
					window.removeEventListener("touchmove", onPinchMove)
					// Flush the final scale — throttle trailing:false means the last value may
					// not have been dispatched yet
					const currentZoom = zoomStateRef.current
					if (currentZoom) {
						setZoomStateRef.current?.({...currentZoom, scale: latestPinchScale.current})
					}
				}
			}

			window.addEventListener("touchmove", onPinchMove, {passive: false})
			window.addEventListener("touchend", onPinchEnd)
			window.addEventListener("touchcancel", onPinchEnd)

		} else if (touches.length === 1 && !isPinching.current && shouldScroll(event)) {
			// Single-finger pan: only when canScroll() allows it (gated by active tool)
			touchStartEvent.preventDefault();
			const startTouch = touches.item(0);
			if (startTouch) {
				let previousClientX = startTouch.clientX;
				let previousClientY = startTouch.clientY;
				panStartVector.current = {
					x: previousClientX,
					y: previousClientY
				}

				const onTouchMove = (touchMoveEvent: TouchEvent) => {
					const moveTouch = touchMoveEvent.touches[0];
					if (moveTouch) {
						handleMove(
							moveTouch.clientX,
							moveTouch.clientY,
							previousClientX,
							previousClientY,
							touchMoveEvent
						);
						previousClientX = moveTouch.clientX;
						previousClientY = moveTouch.clientY;
					}
				};
				const cleanupListeners = () => {
					window.removeEventListener("touchend", onTouchEnd);
					window.removeEventListener("touchcancel", onTouchEnd);
					window.removeEventListener("touchmove", onTouchMove);
				};
				const onTouchEnd = () => {
					setPanInProgress(false);
					cleanupListeners();
					panCleanupListeners.current = null;
				};

				panCleanupListeners.current = cleanupListeners;
				window.addEventListener("touchend", onTouchEnd);
				window.addEventListener("touchcancel", onTouchEnd);
				window.addEventListener("touchmove", onTouchMove, {passive: false});
			}
		}
	};

	return {
		onMouseDown,
		onTouchStart,
		panInProgress,
	}
}